import { LocaleSettings } from "@cf-shared/shared-util";
import { getMeasureUnitConversion } from "util/formatting";
import { equipmentTemplatesEU } from "./equipment-templates-eu";
import { equipmentTemplatesUS } from "./equipment-templates-us";
import { Equipment } from "@cf-shared/data-model/equipment";
import { LL } from 'i18n/i18n.service';
import { deepClone } from "@cf-shared/deep-helpers";

/**
 * Which translated label a starter-equipment template shows.
 *
 * The names are user-visible, so they are NOT written inline in the template
 * files any more — they live in `en.json` and are translated (#4210 Phase E).
 * Each template names its label here instead. The US and EU sets mostly share
 * labels; only the plates template differs ("Plain plates" vs "Free plates"),
 * which is exactly why the key is chosen per template rather than derived from
 * the template id.
 */
export type EquipmentTemplateNameKey =
  | 'barbell'
  | 'dumbbells'
  | 'plainPlates'
  | 'freePlates'
  | 'kettlebells'
  | 'cableMachine'
  | 'customIncrements';

/**
 * `Equipment` is a union — one member per equipment type — so the name has to be
 * dropped from each member separately. A plain `Omit<Equipment, 'name'>` would
 * collapse the union down to the fields they all share and lose `plates`,
 * `stack` and friends.
 */
type EquipmentWithoutName<T> = T extends unknown ? Omit<T, 'name'> : never;

/**
 * A starter-equipment template as written in `equipment-templates-{us,eu}.ts`.
 *
 * It is an `Equipment` with the name replaced by the key of its translated
 * label. `localizeEquipmentTemplates` turns it back into a plain `Equipment` by
 * looking that label up, so nothing downstream has to know templates are
 * special.
 */
export type EquipmentTemplate = EquipmentWithoutName<Equipment> & { nameKey: EquipmentTemplateNameKey };

/**
 * The translated name one starter-equipment template shows.
 *
 * Switch-on-literal with a `never` default, matching `fieldDiffLabel` in
 * services/exercise-diff-ui.ts: it keeps typesafe-i18n's typed accessors, and
 * the default becomes a compile error the moment a template key is added
 * without a label here.
 */
function getEquipmentTemplateLabel(nameKey: EquipmentTemplateNameKey): string {
  const labels = LL.equipment.templates;
  switch (nameKey) {
    case 'barbell': return labels.barbell();
    case 'dumbbells': return labels.dumbbells();
    case 'plainPlates': return labels.plainPlates();
    case 'freePlates': return labels.freePlates();
    case 'kettlebells': return labels.kettlebells();
    case 'cableMachine': return labels.cableMachine();
    case 'customIncrements': return labels.customIncrements();
    default: { const _: never = nameKey; return _; }
  }
}

/**
 * Turn the raw templates into ready-to-use `Equipment`, with the name in the
 * user's language and each template marked as being based on itself.
 *
 * Every template is deep-cloned. A caller receiving one of these usually goes on
 * to build the user's own equipment out of it, and a shallow copy would still
 * share the nested `plates` / `fixed` / `stack` arrays with the module-level
 * constants — so one caller editing a plate in place would corrupt the template
 * for the rest of the session. Cloning six small objects on a page load is far
 * cheaper than that class of bug.
 */
export function localizeEquipmentTemplates(templates: EquipmentTemplate[]): Equipment[] {
  return templates.map(template => {
    const { nameKey, ...rest } = deepClone(template);
    // the cast puts back exactly what `EquipmentWithoutName` took away: `rest` is
    // one whole equipment member minus its name, and the name goes straight back
    // on. TypeScript cannot follow that through a spread of a union type.
    return {
      ...rest,
      name: getEquipmentTemplateLabel(nameKey),
      // a template is based on itself
      basedOnTemplateId: template.id,
    } as Equipment;
  });
}

/**
 * The starter equipment to offer this user: the US set for pounds, the EU set for
 * kilograms, with every name already in the user's language.
 */
export function getEquipmentTemplates(locale: LocaleSettings): Equipment[] {
  const { unitName: weightUnitName } = getMeasureUnitConversion('addedweight_kg', locale, undefined);
  const templates = weightUnitName === 'lb' ? equipmentTemplatesUS : equipmentTemplatesEU;
  return localizeEquipmentTemplates(templates);
}
